import { useCallback, useEffect, useRef, useState } from "react";
import { useMainStore } from "@/stores";
import { useWhatsNewStore } from "@/stores/useWhatsNewStore";
import { APP_VERSION, BUILD_SHA, UPDATES, updatesSince } from "@/shared/utils/appVersion";
import { showInfo } from "@/shared/utils/toast";
import WhatsNewPanel from "./WhatsNewPanel";
import WhatsNewSpotlight from "./WhatsNewSpotlight";

const UPDATE_CHECK_MS = 5 * 60 * 1000;
const UPDATE_TOAST_ID = "app-update-available";
const seenKey = (userId) => `salesworx:whatsNew:lastSeen:${userId}`;

function readSeen(userId) {
  try {
    return localStorage.getItem(seenKey(userId));
  } catch {
    return null;
  }
}

function writeSeen(userId, version) {
  try {
    localStorage.setItem(seenKey(userId), version);
  } catch {
    // Private mode / blocked storage: the window may show again next time.
  }
}

/** Polls the live build's version.json; when a newer build is live, offers a reload once. */
export function useUpdateAvailable(buildSha = BUILD_SHA) {
  const shown = useRef(false);
  useEffect(() => {
    if (!buildSha || buildSha === "dev" || buildSha === "local") return undefined;
    const check = async () => {
      if (shown.current || document.visibilityState !== "visible") return;
      try {
        const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) return;
        const live = await response.json();
        if (!live?.sha || live.sha === buildSha) return;
        shown.current = true;
        showInfo(
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold">A new version of SalesWorx is available</p>
            <p className="text-xs text-gray-500">Reload to get the latest updates.</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="self-start rounded-md bg-secondary px-3 py-1 text-xs font-medium text-white"
            >
              Reload
            </button>
          </div>,
          null,
          { toastId: UPDATE_TOAST_ID, autoClose: false, closeOnClick: false },
        );
      } catch {
        // Offline or blocked: try again later.
      }
    };
    const interval = setInterval(check, UPDATE_CHECK_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
    };
  }, [buildSha]);
}

/**
 * "What's new", the way product-updates feeds work: updates since the user's last visit count as
 * unread (badge in the sidebar); opening the feed reads them. A big launch (`spotlight`) also
 * gets one small card, once. New users start from the current version, with nothing unread.
 * Also offers a reload when a newer build goes live.
 */
export default function WhatsNew({ leftOffset = 256 }) {
  const userId = useMainStore((state) => state.user?.id);
  const { isOpen, spotlight, setUnread, setSpotlight, open, close } = useWhatsNewStore();
  // What was unread when the feed opened: those cards keep their "New" mark while it is open.
  const [newIds, setNewIds] = useState([]);
  useUpdateAvailable();

  useEffect(() => {
    if (!userId || UPDATES.length === 0) return;
    const lastSeen = readSeen(userId);
    if (!lastSeen) {
      writeSeen(userId, APP_VERSION);
      return;
    }
    const fresh = updatesSince(lastSeen);
    setUnread(fresh);
    setSpotlight(fresh.find((u) => u.spotlight) || null);
  }, [userId, setUnread, setSpotlight]);

  const markRead = useCallback(() => {
    if (userId) writeSeen(userId, APP_VERSION);
    setUnread([]);
    setSpotlight(null);
  }, [userId, setUnread, setSpotlight]);

  // Opening the feed reads everything in it.
  useEffect(() => {
    if (!isOpen) return;
    setNewIds(useWhatsNewStore.getState().unread.map((u) => u.id));
    markRead();
  }, [isOpen, markRead]);

  return (
    <>
      <WhatsNewPanel isOpen={isOpen} updates={UPDATES} newIds={newIds} onClose={close} />
      {!isOpen && (
        <WhatsNewSpotlight update={spotlight} leftOffset={leftOffset} onDismiss={markRead} onSeeAll={open} />
      )}
    </>
  );
}
